/**
 * CanvasProvider — wraps Google's A2UIProvider with BeeOS-specific
 * extensions: presentation metadata, awareness, presence, editors,
 * pending state, surface reference, and canvas toggle.
 */
"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import {
  A2UIProvider,
  useA2UIActions,
  ComponentRegistry,
  registerDefaultCatalog,
  type Types,
} from "@a2ui/react";
import { RelayExtensions } from "../relay-extensions";
import { processACPMessage, processRelayMessage } from "../transport-adapter";
import { CanvasResourceResolverProvider, type CanvasResourceResolver } from "./resource-resolver";
import type {
  EditorAwareness,
  EditorSnapshot,
  SurfacePresentation,
  ACPCanvasUpdateEnvelope,
} from "../types";
import {
  BeeOSText,
  BeeOSButton,
  BeeOSCard,
  BeeOSTextField,
  BeeOSCheckBox,
  BeeOSMultipleChoice,
  BeeOSSlider,
  BeeOSTabs,
  BeeOSImage,
  BeeOSIcon,
  BeeOSDivider,
  BeeOSVideo,
  BeeOSAudioPlayer,
  BeeOSRow,
  BeeOSColumn,
  BeeOSList,
  BeeOSModal,
  BeeOSDateTimeInput,
  BeeOSChart,
} from "./components";

// BeeOS component registration map — shared between the explicit beeosRegistry
// (passed to A2UIRenderer root) and the singleton override (for nested
// ComponentNodes that lack a registry prop).
const BEEOS_COMPONENTS: [string, { component: React.ComponentType<any> }][] = [
  ["Text", { component: BeeOSText }],
  ["Button", { component: BeeOSButton }],
  ["Card", { component: BeeOSCard }],
  ["TextField", { component: BeeOSTextField }],
  ["CheckBox", { component: BeeOSCheckBox }],
  ["MultipleChoice", { component: BeeOSMultipleChoice }],
  ["Slider", { component: BeeOSSlider }],
  ["Tabs", { component: BeeOSTabs }],
  ["Image", { component: BeeOSImage }],
  ["Icon", { component: BeeOSIcon }],
  ["Divider", { component: BeeOSDivider }],
  ["Video", { component: BeeOSVideo }],
  ["AudioPlayer", { component: BeeOSAudioPlayer }],
  ["Row", { component: BeeOSRow }],
  ["Column", { component: BeeOSColumn }],
  ["List", { component: BeeOSList }],
  ["Modal", { component: BeeOSModal }],
  ["DateTimeInput", { component: BeeOSDateTimeInput }],
  ["Chart", { component: BeeOSChart }],
];

function applyBeeOSOverrides(registry: ComponentRegistry) {
  for (const [name, reg] of BEEOS_COMPONENTS) {
    // @a2ui/react types are compiled against React 18; cast to satisfy React 19
    registry.register(name, reg as any);
  }
}

// Independent registry — passed to A2UIRenderer via the registry prop (root level).
export const beeosRegistry = new ComponentRegistry();
registerDefaultCatalog(beeosRegistry);
applyBeeOSOverrides(beeosRegistry);

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface CanvasContextValue {
  presentations: Map<string, SurfacePresentation>;
  setPresentations: React.Dispatch<React.SetStateAction<Map<string, SurfacePresentation>>>;
  editorAwareness: Map<string, EditorAwareness>;
  agentPresence: Map<string, boolean>;
  editors: EditorSnapshot[];
  canvasAvailable: boolean;
  canvasEnabled: boolean;
  setCanvasEnabled: (enabled: boolean) => void;
  sendAction: (surfaceId: string, action: Types.A2UIClientEventMessage) => void;
  clearCanvas: () => void;
  deleteSurface: (surfaceId: string) => void;
  referenceSurface: (surfaceId: string) => void;
  referencedSurfaceId: string | null;
  clearReference: () => void;
  pendingSurfaces: Set<string>;
  clearPending: (surfaceId: string) => void;
  relayExtensions: RelayExtensions;
}

const CanvasContext = createContext<CanvasContextValue | null>(null);

export function useCanvas(): CanvasContextValue {
  const ctx = useContext(CanvasContext);
  if (!ctx) throw new Error("useCanvas must be used within a <CanvasProvider>");
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface CanvasProviderProps {
  children: ReactNode;
  sessionId: string | null;
  canvasAvailable?: boolean;
  resolveResource?: CanvasResourceResolver;
  onSendAction?: (surfaceId: string, action: Types.A2UIClientEventMessage) => void;
  onCanvasToggle?: (enabled: boolean) => void;
  onClearCanvas?: () => void;
  onDeleteSurface?: (surfaceId: string) => void;
  onReferenceSurface?: (surfaceId: string | null) => void;
}

const CANVAS_ENABLED_KEY = "beeos_canvas_enabled";

type LocalDeleteFn = (surfaceId: string) => void;
type LocalClearFn = () => void;

function readCanvasEnabledDefault(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage?.getItem(CANVAS_ENABLED_KEY) !== "false";
  } catch {
    return true;
  }
}

function persistCanvasEnabled(enabled: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.setItem(CANVAS_ENABLED_KEY, String(enabled));
  } catch {
    // Canvas still works without persisted preference storage.
  }
}

// Sync BeeOS overrides onto the global singleton registry so that nested
// ComponentNode instances (which don't receive the registry prop) resolve
// to BeeOS components instead of Google defaults.
//
// A2UIProvider.ensureInitialized() runs synchronously during its render and
// populates the singleton with Google defaults. This component renders as a
// child of A2UIProvider, so by the time it executes the singleton is ready.
// We run synchronously during render (not in useEffect) to guarantee the
// overrides are in place before any A2UIRenderer/ComponentNode children mount.
let singletonSynced = false;
function SingletonRegistrySync() {
  if (!singletonSynced) {
    singletonSynced = true;
    applyBeeOSOverrides(ComponentRegistry.getInstance());
  }
  return null;
}

function LocalActionBridge({
  deleteRef,
  clearRef,
}: {
  deleteRef: React.MutableRefObject<LocalDeleteFn | null>;
  clearRef: React.MutableRefObject<LocalClearFn | null>;
}) {
  const { processMessages, getSurfaces } = useA2UIActions();

  useEffect(() => {
    deleteRef.current = (surfaceId: string) => {
      processMessages([{ deleteSurface: { surfaceId } }]);
    };
    clearRef.current = () => {
      const all = getSurfaces();
      const msgs: { deleteSurface: { surfaceId: string } }[] = [];
      for (const [sid] of all) msgs.push({ deleteSurface: { surfaceId: sid } });
      if (msgs.length > 0) processMessages(msgs);
    };
    return () => {
      deleteRef.current = null;
      clearRef.current = null;
    };
  }, [deleteRef, clearRef, processMessages, getSurfaces]);

  return null;
}

export function CanvasProvider({
  children,
  sessionId,
  canvasAvailable = false,
  resolveResource,
  onSendAction,
  onCanvasToggle,
  onClearCanvas,
  onDeleteSurface,
  onReferenceSurface,
}: CanvasProviderProps) {
  const [presentations, setPresentations] = useState<Map<string, SurfacePresentation>>(() => new Map());
  const [editorAwareness, setEditorAwareness] = useState<Map<string, EditorAwareness>>(() => new Map());
  const [agentPresence, setAgentPresence] = useState<Map<string, boolean>>(() => new Map());
  const [editors, setEditors] = useState<EditorSnapshot[]>([]);
  const [referencedSurfaceId, setReferencedSurfaceId] = useState<string | null>(null);
  const [pendingSurfaces, setPendingSurfaces] = useState<Set<string>>(() => new Set());
  const pendingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const localDeleteRef = useRef<LocalDeleteFn | null>(null);
  const localClearRef = useRef<LocalClearFn | null>(null);

  const [canvasEnabled, setCanvasEnabledState] = useState(readCanvasEnabledDefault);

  const relayExtensions = useMemo(() => new RelayExtensions(), []);

  useEffect(() => {
    const unsubs = [
      relayExtensions.subscribeAwareness((a) => setEditorAwareness(new Map(a))),
      relayExtensions.subscribePresence((p) => setAgentPresence(new Map(p))),
      relayExtensions.subscribeEditors((e) => setEditors(e)),
    ];
    return () => unsubs.forEach((u) => u());
  }, [relayExtensions]);

  useEffect(() => {
    localClearRef.current?.();
    setPresentations(new Map());
    setReferencedSurfaceId(null);
    for (const timer of pendingTimersRef.current.values()) clearTimeout(timer);
    pendingTimersRef.current.clear();
    setPendingSurfaces(new Set());
    relayExtensions.reset();
  }, [sessionId, relayExtensions]);

  const setCanvasEnabled = useCallback((enabled: boolean) => {
    setCanvasEnabledState(enabled);
    persistCanvasEnabled(enabled);
    onCanvasToggle?.(enabled);
  }, [onCanvasToggle]);

  const clearPending = useCallback((surfaceId: string) => {
    const timer = pendingTimersRef.current.get(surfaceId);
    if (timer) {
      clearTimeout(timer);
      pendingTimersRef.current.delete(surfaceId);
    }
    setPendingSurfaces((prev) => {
      if (!prev.has(surfaceId)) return prev;
      const next = new Set(prev);
      next.delete(surfaceId);
      return next;
    });
  }, []);

  const sendAction = useCallback((surfaceId: string, action: Types.A2UIClientEventMessage) => {
    if (!onSendAction) return;
    setPendingSurfaces((prev) => {
      const next = new Set(prev);
      next.add(surfaceId);
      return next;
    });
    const oldTimer = pendingTimersRef.current.get(surfaceId);
    if (oldTimer) clearTimeout(oldTimer);
    const timer = setTimeout(() => {
      pendingTimersRef.current.delete(surfaceId);
      setPendingSurfaces((prev) => {
        if (!prev.has(surfaceId)) return prev;
        const next = new Set(prev);
        next.delete(surfaceId);
        return next;
      });
    }, 30_000);
    pendingTimersRef.current.set(surfaceId, timer);
    onSendAction(surfaceId, action);
  }, [onSendAction]);

  const clearCanvas = useCallback(() => {
    localClearRef.current?.();
    setPresentations(new Map());
    onClearCanvas?.();
  }, [onClearCanvas]);

  const deleteSurface = useCallback((surfaceId: string) => {
    localDeleteRef.current?.(surfaceId);
    setPresentations((prev) => {
      if (!prev.has(surfaceId)) return prev;
      const next = new Map(prev);
      next.delete(surfaceId);
      return next;
    });
    onDeleteSurface?.(surfaceId);
  }, [onDeleteSurface]);

  const referenceSurface = useCallback((surfaceId: string) => {
    setReferencedSurfaceId((prev) => {
      const next = prev === surfaceId ? null : surfaceId;
      onReferenceSurface?.(next);
      return next;
    });
  }, [onReferenceSurface]);

  const clearReference = useCallback(() => {
    setReferencedSurfaceId(null);
  }, []);

  const handleGoogleAction = useCallback((message: Types.A2UIClientEventMessage) => {
    const envelope = message as { userAction?: { surfaceId?: string; name?: string; sourceComponentId?: string; timestamp?: string; context?: Record<string, unknown> } };
    const ua = envelope.userAction;
    if (!ua) return;
    sendAction(ua.surfaceId ?? "", message);
  }, [sendAction]);

  const value = useMemo<CanvasContextValue>(() => ({
    presentations,
    setPresentations,
    editorAwareness,
    agentPresence,
    editors,
    canvasAvailable,
    canvasEnabled,
    setCanvasEnabled,
    sendAction,
    clearCanvas,
    deleteSurface,
    referenceSurface,
    referencedSurfaceId,
    clearReference,
    pendingSurfaces,
    clearPending,
    relayExtensions,
  }), [
    presentations,
    setPresentations,
    editorAwareness,
    agentPresence,
    editors,
    canvasAvailable,
    canvasEnabled,
    setCanvasEnabled,
    sendAction,
    clearCanvas,
    deleteSurface,
    referenceSurface,
    referencedSurfaceId,
    clearReference,
    pendingSurfaces,
    clearPending,
    relayExtensions,
  ]);

  return (
    <CanvasContext.Provider value={value}>
      <CanvasResourceResolverProvider resolveResource={resolveResource}>
        <A2UIProvider onAction={handleGoogleAction}>
          <SingletonRegistrySync />
          <LocalActionBridge deleteRef={localDeleteRef} clearRef={localClearRef} />
          {children as any}
        </A2UIProvider>
      </CanvasResourceResolverProvider>
    </CanvasContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Message handler hooks
// ---------------------------------------------------------------------------

export function useCanvasMessageHandler() {
  const { canvasEnabled, setPresentations } = useCanvas();
  const { processMessages, getSurfaces } = useA2UIActions();

  return useCallback(
    (update: Record<string, unknown>) => {
      if (!canvasEnabled) return;
      if (update.sessionUpdate === "canvas.update") {
        processACPMessage(
          {
            processMessages,
            onPresentationChange: (sid: string, pres: SurfacePresentation) => {
              setPresentations((prev) => {
                const next = new Map(prev);
                next.set(sid, pres);
                return next;
              });
            },
          },
          update as unknown as ACPCanvasUpdateEnvelope,
        );
      } else if (update.sessionUpdate === "canvas.reset") {
        const surfaceId = update.surfaceId as string | undefined;
        if (surfaceId) {
          processMessages([{ deleteSurface: { surfaceId } }]);
          setPresentations((prev) => {
            if (!prev.has(surfaceId)) return prev;
            const next = new Map(prev);
            next.delete(surfaceId);
            return next;
          });
        } else {
          const allSurfaces = getSurfaces();
          const msgs: { deleteSurface: { surfaceId: string } }[] = [];
          for (const [sid] of allSurfaces) msgs.push({ deleteSurface: { surfaceId: sid } });
          if (msgs.length > 0) processMessages(msgs);
          setPresentations(new Map());
        }
      }
    },
    [canvasEnabled, processMessages, getSurfaces, setPresentations],
  );
}

export function useRelayMessageHandler() {
  const { relayExtensions, setPresentations } = useCanvas();
  const { processMessages } = useA2UIActions();

  return useCallback(
    (data: Uint8Array) => {
      if (relayExtensions.processMessage(data)) return true;
      return processRelayMessage(
        {
          processMessages,
          onPresentationChange: (sid, pres) => {
            setPresentations((prev) => {
              const next = new Map(prev);
              next.set(sid, pres);
              return next;
            });
          },
        },
        data,
      );
    },
    [relayExtensions, processMessages, setPresentations],
  );
}
